import logger from '../utils/logger';
import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { settingsService } from '../services/settingsService';
import { connectionService } from '../services/connectionService';
import { useSettings } from './SettingsContext';

const ConnectionContext = createContext();

export const useConnection = () => {
  const context = useContext(ConnectionContext);
  if (!context) {
    throw new Error('useConnection must be used within a ConnectionProvider');
  }
  return context;
};

export const ConnectionProvider = ({ children }) => {
  const { connectionSettings, isLoading: settingsLoading } = useSettings();
  const [settings, setSettings] = useState({
    // Pool settings
    default_pool_size: 10,
    max_pool_size: 50,
    min_pool_size: 5,
    pool_timeout: 30000,
    connection_test_query: 'SELECT 1',
    test_on_borrow: true,
    test_on_return: false,
    test_while_idle: true,
    
    // Connection settings
    connection_timeout: 30000,
    socket_timeout: 60000,
    validation_timeout: 5000,
    max_lifetime: 1800000, // 30 minutes
    idle_timeout: 600000, // 10 minutes
    
    // Security settings
    enable_ssl: false,
    ssl_mode: 'prefer',
    trust_server_certificate: false,
    encrypt_connection: false,
    
    // Performance settings
    enable_connection_pooling: true,
    connection_cache_statements: true,
    cache_prep_stmts: true,
    prep_stmt_cache_size: 250,
    prep_stmt_cache_sql_limit: 2048,
    
    // Monitoring settings
    enable_connection_monitoring: true,
    log_slow_queries: true,
    slow_query_threshold: 5000,
    enable_leak_detection: true,
    leak_detection_threshold: 60000,
    
    // Retry settings
    enable_auto_reconnect: true,
    max_reconnect_attempts: 3,
    reconnect_interval: 5000,
    
    // Health check settings
    enable_health_checks: true,
    health_check_interval: 30000,
    health_check_timeout: 5000
  });
  
  const monitoringIntervalsRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [connections, setConnections] = useState([]);
  const [connectionStats, setConnectionStats] = useState({
    total: 0,
    active: 0,
    idle: 0,
    failed: 0,
    avgResponseTime: 0
  });
  const [poolMetrics, setPoolMetrics] = useState({});

  // Load connection settings
  // Load connection settings from centralized SettingsContext
  useEffect(() => {
    if (!settingsLoading && connectionSettings) {
      setSettings(prev => ({
        ...prev,
        ...connectionSettings
      }));
      setIsLoading(false);
    }
  }, [connectionSettings, settingsLoading]);

  // Load stats and start monitoring
  useEffect(() => {
    if (!isLoading) {
      loadConnectionStats();
      const cleanup = startMonitoring();
      return cleanup;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  const loadConnectionStats = async () => {
    try {
      const connections = await connectionService.getConnections();
      setConnections(connections);
      
      // TODO: Get detailed connection statistics (endpoint not implemented yet)
      // Temporarily disabled to prevent 404 errors
      // const response = await fetch('/api/connections/stats');
      // if (response.ok) {
      //   const stats = await response.json();
      //   setConnectionStats(stats);
      // }
      
      // TODO: Get pool metrics for each connection (endpoint not implemented yet)
      // Temporarily disabled to prevent 404 errors
      const poolMetrics = {};
      // for (const conn of connections) {
      //   try {
      //     const poolResponse = await fetch(`/api/connections/${conn.id}/pool-metrics`);
      //     if (poolResponse.ok) {
      //       poolMetrics[conn.id] = await poolResponse.json();
      //     }
      //   } catch (error) {
      //     logger.warn(`Failed to get pool metrics for connection ${conn.id}:`, error);
      //   }
      // }
      setPoolMetrics(poolMetrics);
    } catch (error) {
      logger.warn('Could not load connection stats:', error);
    }
  };

  // Update connection settings
  const updateConnectionSettings = async (newSettings) => {
    try {
      const updatedSettings = { ...settings, ...newSettings };
      setSettings(updatedSettings);
      
      // Save to backend
      await settingsService.updateSettingsBulk('connection', updatedSettings);
      
      // Apply settings to existing connections
      await applySettingsToConnections(newSettings);
    } catch (error) {
      logger.error('Failed to save connection settings:', error);
      // Revert on error
      await loadConnectionSettings();
    }
  };

  // Apply settings to existing connections
  const applySettingsToConnections = async (newSettings) => {
    try {
      // Update pool settings for all connections
      await fetch('/api/connections/pool-config', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(newSettings)
      });
      
      // Reload connections to reflect changes
      await loadConnectionStats();
    } catch (error) {
      logger.warn('Could not apply settings to connections:', error);
    }
  };

  // Get default connection configuration
  const getDefaultConnectionConfig = useCallback(() => {
    return {
      poolConfig: {
        initialSize: settings.min_pool_size,
        maxTotal: settings.max_pool_size,
        maxIdle: Math.floor(settings.max_pool_size * 0.8),
        minIdle: settings.min_pool_size,
        maxWaitMillis: settings.pool_timeout,
        validationQuery: settings.connection_test_query,
        testOnBorrow: settings.test_on_borrow,
        testOnReturn: settings.test_on_return,
        testWhileIdle: settings.test_while_idle,
        timeBetweenEvictionRunsMillis: 30000,
        minEvictableIdleTimeMillis: settings.idle_timeout,
        maxConnLifetimeMillis: settings.max_lifetime
      },
      connectionConfig: {
        connectTimeout: settings.connection_timeout,
        socketTimeout: settings.socket_timeout,
        validationTimeout: settings.validation_timeout,
        enableSSL: settings.enable_ssl,
        sslMode: settings.ssl_mode,
        trustServerCertificate: settings.trust_server_certificate
      },
      performanceConfig: {
        cachePreparedStatements: settings.cache_prep_stmts,
        preparedStatementCacheSize: settings.prep_stmt_cache_size,
        preparedStatementCacheSqlLimit: settings.prep_stmt_cache_sql_limit,
        enableConnectionPooling: settings.enable_connection_pooling
      },
      monitoringConfig: {
        enableMonitoring: settings.enable_connection_monitoring,
        logSlowQueries: settings.log_slow_queries,
        slowQueryThreshold: settings.slow_query_threshold,
        enableLeakDetection: settings.enable_leak_detection,
        leakDetectionThreshold: settings.leak_detection_threshold
      },
      retryConfig: {
        enableAutoReconnect: settings.enable_auto_reconnect,
        maxReconnectAttempts: settings.max_reconnect_attempts,
        reconnectInterval: settings.reconnect_interval
      }
    };
  }, [settings]);

  // Create connection with default settings
  const createConnectionWithDefaults = async (connectionData) => {
    const defaultConfig = getDefaultConnectionConfig();
    const connectionWithDefaults = {
      ...connectionData,
      config: {
        ...defaultConfig,
        ...connectionData.config
      }
    };
    
    try {
      const result = await connectionService.createConnection(connectionWithDefaults);
      await loadConnectionStats(); // Refresh stats
      return result;
    } catch (error) {
      logger.error('Failed to create connection:', error);
      throw error;
    }
  };

  // Test connection with settings applied
  const testConnectionWithSettings = async (connectionData) => {
    const defaultConfig = getDefaultConnectionConfig();
    const testConfig = {
      ...connectionData,
      config: {
        ...defaultConfig.connectionConfig,
        ...connectionData.config
      }
    };

    try {
      const result = await connectionService.testConnection(testConfig);
      return result;
    } catch (error) {
      logger.error('Failed to test connection:', error);
      throw error;
    }
  };

  // Monitor connection health
  const monitorConnectionHealth = useCallback(async () => {
    if (!settings.enable_health_checks) return [];
    
    try {
      const healthChecks = await Promise.all(
        connections.map(async (conn) => {
          try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), settings.health_check_timeout || 5000);
            const response = await fetch(`/api/connections/${conn.id}/health`, {
              credentials: 'include',
              signal: controller.signal
            });
            clearTimeout(timeoutId);
            return {
              id: conn.id,
              name: conn.name,
              healthy: response.ok,
              responseTime: response.headers.get('X-Response-Time') || 0,
              lastCheck: new Date().toISOString()
            };
          } catch (error) {
            return {
              id: conn.id,
              name: conn.name,
              healthy: false,
              error: error.message,
              lastCheck: new Date().toISOString()
            };
          }
        })
      );
      return healthChecks;
    } catch (error) {
      logger.warn('Failed to perform health checks:', error);
      return [];
    }
  }, [connections, settings.enable_health_checks, settings.health_check_timeout]);

  // Start monitoring
  const startMonitoring = useCallback(() => {
    if (!settings.enable_connection_monitoring) return;

    // Clean up any existing intervals
    if (monitoringIntervalsRef.current) {
      Object.values(monitoringIntervalsRef.current).forEach(clearInterval);
    }

    // Set up intervals for monitoring
    const statsInterval = setInterval(loadConnectionStats, 30000); // Every 30 seconds
    const healthInterval = setInterval(monitorConnectionHealth, settings.health_check_interval);

    // Store interval IDs in ref for cleanup
    monitoringIntervalsRef.current = { statsInterval, healthInterval };

    return () => {
      clearInterval(statsInterval);
      clearInterval(healthInterval);
      monitoringIntervalsRef.current = null;
    };
  }, [settings.enable_connection_monitoring, settings.health_check_interval, loadConnectionStats, monitorConnectionHealth]);

  // Stop monitoring
  const stopMonitoring = useCallback(() => {
    if (monitoringIntervalsRef.current) {
      Object.values(monitoringIntervalsRef.current).forEach(clearInterval);
      monitoringIntervalsRef.current = null;
    }
  }, []);

  // Get connection recommendations
  const getConnectionRecommendations = useCallback(() => {
    const recommendations = [];

    // Pool size recommendations
    if (connectionStats.active / settings.max_pool_size > 0.8) {
      recommendations.push({
        type: 'warning',
        category: 'performance',
        title: 'Connection Pool Nearly Full',
        description: `Using ${connectionStats.active}/${settings.max_pool_size} connections. Consider increasing pool size.`,
        action: 'increase_pool_size'
      });
    }

    // Response time recommendations
    if (connectionStats.avgResponseTime > 5000) {
      recommendations.push({
        type: 'error',
        category: 'performance',
        title: 'Slow Connection Response',
        description: `Average response time is ${connectionStats.avgResponseTime}ms. Check network or database performance.`,
        action: 'optimize_connections'
      });
    }

    // Failed connections
    if (connectionStats.failed > 0) {
      recommendations.push({
        type: 'error',
        category: 'reliability',
        title: 'Connection Failures',
        description: `${connectionStats.failed} connections have failed. Check connection settings and network.`,
        action: 'check_failed_connections'
      });
    }

    // SSL recommendations
    if (!settings.enable_ssl && connections.some(conn => conn.host && !conn.host.includes('localhost'))) {
      recommendations.push({
        type: 'info',
        category: 'security',
        title: 'Consider Enabling SSL',
        description: 'For remote connections, consider enabling SSL for better security.',
        action: 'enable_ssl'
      });
    }

    return recommendations;
  }, [settings, connectionStats, connections]);

  // Apply recommendation
  const applyRecommendation = useCallback(async (action) => {
    switch (action) {
      case 'increase_pool_size':
        await updateConnectionSettings({ 
          max_pool_size: settings.max_pool_size + 10,
          default_pool_size: Math.min(settings.default_pool_size + 5, settings.max_pool_size + 10)
        });
        break;
      case 'enable_ssl':
        await updateConnectionSettings({ enable_ssl: true, ssl_mode: 'require' });
        break;
      case 'optimize_connections':
        await updateConnectionSettings({ 
          connection_timeout: Math.max(settings.connection_timeout - 5000, 5000),
          pool_timeout: Math.max(settings.pool_timeout - 5000, 5000)
        });
        break;
      default:
        logger.warn('Unknown recommendation action:', action);
    }
  }, [settings, updateConnectionSettings]);

  const value = {
    settings,
    isLoading,
    connections,
    connectionStats,
    poolMetrics,
    recommendations: getConnectionRecommendations(),
    
    // Settings management
    updateConnectionSettings,
    
    // Connection operations with settings
    getDefaultConnectionConfig,
    createConnectionWithDefaults,
    testConnectionWithSettings,
    
    // Monitoring
    monitorConnectionHealth,
    startMonitoring,
    stopMonitoring,
    
    // Utilities
    loadConnectionStats,
    applyRecommendation
  };

  return (
    <ConnectionContext.Provider value={value}>
      {children}
    </ConnectionContext.Provider>
  );
};

export default ConnectionContext;